import type { KeycloakTokenParsed } from 'keycloak-js';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import MissingModuleModal from '../components/modals/MissingModuleModal';
import keycloak, { initKeycloak, loginKeycloak, logoutKeycloak, refreshKeycloakAccessToken } from '../keycloak';
import { clearStoredAuthToken } from '../utils/authTokenCookieUtils';
import { SHOP_LINK } from '../utils/constants';
import { getCurrentUrlWithoutExternalPlanQueryParams, storeExternalPlanQueryParamsFromSearch } from '../utils/externalPlanDataStorage';
import filesApiService from '../utils/filesApiService';
import { hasRequiredFloorplannerModule } from '../utils/keycloakUserUtils';
import { clearPendingSaveAfterLogin } from '../utils/pendingSaveAfterLoginStorage';

interface OpenLoginOptions {
  showMissingModuleNotice?: boolean;
}

interface LoginContextType {
  openLoginModal: (onSuccess?: (token?: string) => void, options?: OpenLoginOptions) => Promise<void>;
  closeLoginModal: () => void;
  isLoginModalOpen: boolean;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  token?: string;
  tokenParsed?: KeycloakTokenParsed;
  logout: () => Promise<void>;
  refreshAccessToken: (minValidity?: number) => Promise<string | undefined>;
}

const LoginContext = createContext<LoginContextType>({} as LoginContextType);

export const useLoginContext = () => useContext(LoginContext);

export const LoginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialIsAppAuthenticated = Boolean(keycloak.authenticated && keycloak.tokenParsed && hasRequiredFloorplannerModule(keycloak.tokenParsed));
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(initialIsAppAuthenticated);
  const [token, setToken] = useState<string | undefined>(initialIsAppAuthenticated ? keycloak.token : undefined);
  const [tokenParsed, setTokenParsed] = useState<KeycloakTokenParsed | undefined>(initialIsAppAuthenticated ? keycloak.tokenParsed : undefined);
  const [isMissingModuleModalOpen, setIsMissingModuleModalOpen] = useState(false);
  const pendingLoginSuccessRef = useRef<((token?: string) => void) | undefined>(undefined);
  const missingModuleNoticeDismissedRef = useRef(false);
  const location = useLocation();

  const syncAuthState = useCallback((authenticated = Boolean(keycloak.authenticated), options: OpenLoginOptions = {}) => {
    const { showMissingModuleNotice = true } = options;
    const hasParsedToken = Boolean(keycloak.tokenParsed);
    const isAllowed = !authenticated || (hasParsedToken && hasRequiredFloorplannerModule(keycloak.tokenParsed));
    const isAppAuthenticated = authenticated && isAllowed;

    setIsAuthenticated(isAppAuthenticated);
    setToken(isAppAuthenticated ? keycloak.token : undefined);
    setTokenParsed(isAppAuthenticated ? keycloak.tokenParsed : undefined);

    if (isAppAuthenticated) {
      missingModuleNoticeDismissedRef.current = false;
      setIsMissingModuleModalOpen(false);
    }

    if (authenticated && hasParsedToken && !isAllowed) {
      clearPendingSaveAfterLogin();
      pendingLoginSuccessRef.current = undefined;

      if (showMissingModuleNotice && !missingModuleNoticeDismissedRef.current) {
        setIsMissingModuleModalOpen(true);
      }
    }

    return isAppAuthenticated;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const handleAuthenticated = () => {
      if (!isMounted) return;
      const isAppAuthenticated = syncAuthState(true);
      if (!isAppAuthenticated) {
        pendingLoginSuccessRef.current = undefined;
        return;
      }
      pendingLoginSuccessRef.current?.(keycloak.token);
      pendingLoginSuccessRef.current = undefined;
    };

    const handleLoggedOut = () => {
      if (!isMounted) return;
      syncAuthState(false);
    };

    keycloak.onAuthSuccess = handleAuthenticated;
    keycloak.onAuthRefreshSuccess = handleAuthenticated;
    keycloak.onAuthLogout = handleLoggedOut;
    keycloak.onTokenExpired = () => {
      refreshKeycloakAccessToken()
        .then(() => {
          if (isMounted) {
            syncAuthState(Boolean(keycloak.authenticated));
          }
        })
        .catch(() => {
          if (isMounted) {
            syncAuthState(false);
          }
        });
    };

    storeExternalPlanQueryParamsFromSearch();
    initKeycloak()
      .then((authenticated) => {
        if (!isMounted) return;

        syncAuthState(authenticated);
        setIsAuthReady(true);
      })
      .catch((error) => {
        console.error('Keycloak init failed:', error);
        if (!isMounted) return;
        syncAuthState(false);
        setIsAuthReady(true);
      });

    const refreshInterval = window.setInterval(() => {
      if (!keycloak.authenticated || !hasRequiredFloorplannerModule(keycloak.tokenParsed)) return;
      refreshKeycloakAccessToken()
        .then(() => {
          if (isMounted) {
            syncAuthState(true);
          }
        })
        .catch(() => {
          if (isMounted) {
            syncAuthState(false);
          }
        });
    }, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshInterval);
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
    };
  }, [syncAuthState]);

  const openLoginModal = useCallback(
    async (onSuccess?: (token?: string) => void, options: OpenLoginOptions = {}): Promise<void> => {
      const { showMissingModuleNotice = true } = options;
      if (showMissingModuleNotice) {
        missingModuleNoticeDismissedRef.current = false;
      }

      pendingLoginSuccessRef.current = onSuccess;
      storeExternalPlanQueryParamsFromSearch();

      try {
        await initKeycloak();
      } catch (error) {
        console.warn('Keycloak init failed before login, continuing with explicit login redirect:', error);
        syncAuthState(false, { showMissingModuleNotice });
      }

      if (keycloak.authenticated) {
        try {
          const refreshedToken = await refreshKeycloakAccessToken();
          const isAppAuthenticated = syncAuthState(true, { showMissingModuleNotice });
          pendingLoginSuccessRef.current = undefined;
          if (isAppAuthenticated) {
            onSuccess?.(refreshedToken || keycloak.token);
          }
          return;
        } catch (error) {
          console.warn('Keycloak token refresh failed before login, redirecting to login:', error);
          syncAuthState(false, { showMissingModuleNotice });
        }
      }

      try {
        await loginKeycloak({
          redirectUri: getCurrentUrlWithoutExternalPlanQueryParams() || window.location.href,
        });
      } catch (error) {
        pendingLoginSuccessRef.current = undefined;
        console.error('Keycloak login redirect failed:', error);
      }
    },
    [syncAuthState],
  );

  const closeLoginModal = () => {
    if (window.location.pathname === '/login') {
      window.history.replaceState(null, '', '/');
    }
  };

  const continueInDemoMode = useCallback(() => {
    missingModuleNoticeDismissedRef.current = true;
    pendingLoginSuccessRef.current = undefined;
    setIsAuthenticated(false);
    setToken(undefined);
    setTokenParsed(undefined);
    setIsMissingModuleModalOpen(false);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    clearStoredAuthToken();
    pendingLoginSuccessRef.current = undefined;
    await filesApiService.logout();
    await logoutKeycloak({
      redirectUri: getCurrentUrlWithoutExternalPlanQueryParams() || window.location.origin,
    });
  }, []);

  const refreshAccessToken = useCallback(
    async (minValidity?: number): Promise<string | undefined> => {
      try {
        const refreshedToken = await refreshKeycloakAccessToken(minValidity);
        const isAppAuthenticated = syncAuthState(Boolean(keycloak.authenticated));
        return isAppAuthenticated ? refreshedToken : undefined;
      } catch (error) {
        syncAuthState(false);
        throw error;
      }
    },
    [syncAuthState],
  );

  useEffect(() => {
    if (!isAuthReady || location.pathname !== '/login') return;

    if (isAuthenticated) {
      window.history.replaceState(null, '', '/');
      return;
    }

    openLoginModal();
  }, [isAuthReady, isAuthenticated, location.pathname, openLoginModal]);

  return (
    <LoginContext.Provider
      value={{
        openLoginModal,
        closeLoginModal,
        isLoginModalOpen: false,
        isAuthenticated,
        isAuthReady,
        token,
        tokenParsed,
        logout,
        refreshAccessToken,
      }}
    >
      {children}
      <MissingModuleModal isOpen={isMissingModuleModalOpen} onClose={continueInDemoMode} onDemoMode={continueInDemoMode} shopLink={SHOP_LINK} />
    </LoginContext.Provider>
  );
};
